import { Link } from "react-router-dom";
import { BadgeCheck, FileCheck2, Gauge, LayoutTemplate, ShieldCheck } from "lucide-react";
import { WhatsAppCta } from "../components/WhatsAppCta";
import { useSeo } from "../lib/useSeo";

export function WhatsappOficial() {
  useSeo({
    title: "API Oficial do WhatsApp (Meta Cloud API)",
    description:
      "SendCube e Meta Business Partner e integra com a API Oficial do WhatsApp (Meta Cloud API): templates aprovados, numero verificado e envio em escala.",
    path: "/whatsapp-oficial",
  });

  return (
    <>
      <section className="mx-auto max-w-6xl px-5 pb-14 pt-14 md:pt-20">
        <div className="inline-flex items-center gap-2 rounded-full border-2 border-ink bg-white px-3 py-1 text-xs font-bold">
          <BadgeCheck size={14} className="text-brand-600" /> Meta Business Partner
        </div>
        <h1 className="mt-5 max-w-[26ch] text-4xl font-extrabold leading-[1.1] md:text-5xl">
          Integração direta com a Meta
        </h1>
        <p className="mt-5 max-w-[62ch] text-lg text-ink/70">
          A SendCube é parceira oficial da Meta com integração com a API Oficial do WhatsApp (Cloud
          API) - o mesmo canal que grandes empresas usam para notificar clientes, com número
          verificado e templates aprovados pela própria Meta.
        </p>
        <div className="mt-8">
          <WhatsAppCta message="Ola! Quero configurar a API Oficial do WhatsApp na SendCube.">
            Quero configurar a API Oficial
          </WhatsAppCta>
        </div>
      </section>

      <section className="border-y-2 border-ink bg-white py-14">
        <div className="mx-auto max-w-6xl px-5">
          <h2 className="text-2xl font-bold">Por que empresas maiores escolhem esse caminho</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            <div className="rounded-2xl border-2 border-ink bg-paper p-6 shadow-solid-sm">
              <ShieldCheck size={24} className="text-brand-600" />
              <h3 className="mt-3 font-bold">Maior volume, sem banimento</h3>
              <p className="mt-2 text-sm text-ink/60">
                O número de envio opera dentro de um limite de mensageria, definido pela própria Meta. Esse limite aumenta, conforme a qualidade e o volume do seu envio - sem depender de
                intervalo manual entre mensagens.
              </p>
            </div>
            <div className="rounded-2xl border-2 border-ink bg-paper p-6 shadow-solid-sm">
              <LayoutTemplate size={24} className="text-brand-600" />
              <h3 className="mt-3 font-bold">Templates com variáveis da planilha</h3>
              <p className="mt-2 text-sm text-ink/60">
                Sincroniza templates já aprovados no seu WhatsApp Business, escolhe qual usar
                na campanha e mapeia cada variável (nome, valor, data...), direto para uma coluna
                da sua planilha.
              </p>
            </div>
            <div className="rounded-2xl border-2 border-ink bg-paper p-6 shadow-solid-sm">
              <Gauge size={24} className="text-brand-600" />
              <h3 className="mt-3 font-bold">FVoltado para alto volume</h3>
              <p className="mt-2 text-sm text-ink/60">
                Como o envio é feito por API (não por um app de celular conectado), a SendCube consegue
                enviar várias mensagens, ao mesmo tempo - o gargalo passa a ser o limite da Meta,
                não a nossa plataforma.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-14">
        <div className="grid items-center gap-10 md:grid-cols-2">
          <div>
            <FileCheck2 size={28} className="text-brand-600" />
            <h2 className="mt-4 text-2xl font-bold">O que você precisa ter em mãos</h2>
            <p className="mt-3 text-ink/60">
              Para a configuração, a Meta exige:
            </p>
            <ul className="mt-4 space-y-2 text-sm text-ink/70">
              <li>• Uma conta comercial, verificada, no WhatsApp Business (WABA) </li>
              <li>• Pelo menos um template de mensagem aprovado pela Meta</li>
              <li>• Um número de telefone dedicado a essa API (não pode ser o mesmo número do app comum)</li>
            </ul>
          </div>
          <div className="rounded-2xl border-2 border-ink bg-ink p-6 text-white shadow-solid">
            <p className="text-xs font-bold uppercase tracking-wide text-white/50">
              Exemplo de template
            </p>
            <div className="mt-4 rounded-lg bg-white/10 p-4 text-sm">
              Olá! {"{{1}}"} Seu pedido {"{{2}}"} foi enviado e chega em até {"{{3}}"}. Qualquer
              dúvida, envie aqui.
            </div>
            <p className="mt-4 text-xs text-white/50">
              Cada {"{{numero}}"} vira uma coluna da sua planilha, mapeada na hora de criar a
              campanha.
            </p>
          </div>
        </div>
      </section>

      <section className="border-t-2 border-ink bg-white py-14">
        <div className="mx-auto max-w-6xl px-5">
          <h2 className="text-2xl font-bold">Dúvidas entre API Web e API Oficial?</h2>
          <p className="mt-2 max-w-[60ch] text-ink/60">
            Não existe resposta única. Tudo depende do seu volume de envio e da urgência para iniciar.
          </p>
          <div className="mt-8 overflow-x-auto rounded-2xl border-2 border-ink">
            <table className="w-full text-left text-sm">
              <thead className="bg-ink text-white">
                <tr>
                  <th className="px-4 py-3 font-bold">&nbsp;</th>
                  <th className="px-4 py-3 font-bold">API Oficial</th>
                  <th className="px-4 py-3 font-bold">API Web (QR Code)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10 bg-paper">
                <tr>
                  <td className="px-4 py-3 font-medium">Setup</td>
                  <td className="px-4 py-3">Requer WABA aprovada pela Meta</td>
                  <td className="px-4 py-3">Escanear QR Code em minutos</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">Risco de bloqueio</td>
                  <td className="px-4 py-3">Não há banimento por volume dentro do limite</td>
                  <td className="px-4 py-3">Existe se enviar rápido demais</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">Mensagem inicial</td>
                  <td className="px-4 py-3">Precisa de template aprovado pela Meta</td>
                  <td className="px-4 py-3">Texto livre, qualquer horário</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">Indicado para</td>
                  <td className="px-4 py-3">Alto volume, empresas maiores</td>
                  <td className="px-4 py-3">Começar rápido, volume moderado</td>
                </tr>
              </tbody>
            </table>
          </div>
          <Link to="/whatsapp-web" className="mt-6 inline-block text-sm font-bold text-brand-600 hover:underline">
            Ver os detalhes da API Web (QR Code) →
          </Link>
        </div>
      </section>
    </>
  );
}
